import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const pipeline = [
  { label: '课堂视频', desc: '原始课堂数据', icon: 'ri-video-line', color: 'primary' },
  { label: '单课报告', desc: '单课评价结果', icon: 'ri-file-list-3-line', color: 'gold' },
  { label: '教师档案', desc: '教师个体汇总', icon: 'ri-user-line', color: 'primary' },
  { label: '教研组统计', desc: '教研组层面汇总', icon: 'ri-team-line', color: 'gold' },
  { label: '学校质量图', desc: '校级数据聚合', icon: 'ri-building-line', color: 'primary' },
];

export default function P75ModelComparison({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative h-screen flex flex-col justify-center overflow-hidden">
      {/* 三层背景光效 */}
      <div ref={orb1Ref} className="absolute top-20 right-24 w-[380px] h-[380px] rounded-full bg-primary-200/30 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-[400px] h-[400px] rounded-full bg-accent-200/25 blur-[110px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />
      <div className="absolute top-1/2 left-1/3 w-[300px] h-[300px] rounded-full bg-secondary-200/20 blur-[140px] pointer-events-none animate-orb-float-3" />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-6 md:py-8">
        <div ref={titleRef} className="text-center mb-6 md:mb-8">
          <span className={`inline-flex items-center gap-2 text-base font-body font-semibold text-gold-600 uppercase tracking-[0.15em] mb-4 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="gold-dot" />数据管道
          </span>
          <h2 className={`font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            数据如何从<span className="bg-gradient-to-r from-primary-500 to-accent-500 bg-clip-text text-transparent">单课</span>进入<span className="text-gold-600">跟踪图</span>
          </h2>
          <p className={`text-base text-foreground-950 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            跟踪图不是重新填写出来的，而是从单课证据逐级汇总、经过口径校验形成的
          </p>
        </div>

        {/* Pipeline: 左侧渐变管道线 + 右侧节点 */}
        <div className={`relative max-w-lg mx-auto transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {/* 左侧渐变竖线 */}
          <div className="absolute left-5 top-4 bottom-24 w-0.5 rounded-full bg-gradient-to-b from-primary-300 via-gold-400 to-accent-400" />

          {pipeline.map((item, i) => (
            <div key={item.label} className="flex items-center gap-4 mb-1">
              {/* 圆点 */}
              <div className="relative z-10 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0
                ${item.color === 'primary' ? 'bg-primary-100 border border-primary-200/50' : 'bg-gold-100 border border-gold-300/50'}"
              >
                <i className={`${item.icon} ${item.color === 'primary' ? 'text-primary-600' : 'text-gold-600'}`} />
              </div>

              {/* 卡片 */}
              <div className="flex-1 p-4 rounded-lg bg-background-100/50 border border-background-200/40 hover:bg-background-100 transition-colors duration-300">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-base font-body font-bold text-foreground-950">{item.label}</span>
                    <span className="text-sm text-foreground-950 font-body ml-2">{item.desc}</span>
                  </div>
                  <div className="w-5 h-5 rounded-full bg-background-200/50 flex items-center justify-center">
                    <i className="ri-arrow-down-line text-xs text-foreground-950" />
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* 口径校验 */}
          <div className="flex items-center gap-4 mb-1">
            <div className="relative z-10 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-gold-50 border border-gold-200/50">
              <i className="ri-shield-check-line text-gold-600" />
            </div>
            <div className="flex-1 py-2 px-3 rounded-lg border border-dashed border-gold-300/40 text-center">
              <span className="text-sm font-body font-bold text-gold-600">口径校验 — 确保跨层级数据一致</span>
            </div>
          </div>

          {/* 最终汇总节点 */}
          <div className="flex items-center gap-4">
            <div className="relative z-10 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 bg-accent-100 border border-accent-300/50 shadow-sm">
              <i className="ri-map-2-line text-xl text-accent-600" />
            </div>
            <div className="flex-1 p-4 rounded-xl bg-accent-500/8 border border-accent-400/25">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-lg font-body font-bold text-accent-700">省域质量图</span>
                  <span className="text-sm text-foreground-950 font-body ml-2">全域数据汇聚与可视化</span>
                </div>
                <span className="px-2.5 py-1 rounded-full text-sm font-body font-bold bg-accent-500 text-background-50 whitespace-nowrap">最终汇总</span>
              </div>
            </div>
          </div>
        </div>

        <div className={`max-w-3xl mx-auto text-center mt-6 md:mt-8 transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="p-5 glass-liquid rounded-xl">
            <p className="text-base text-foreground-950 font-body leading-relaxed">
              每一个趋势、每一个颜色块、每一个管理判断，都能够<strong className="text-gold-700">向下追溯</strong>，必要时一直回到原始课堂片段。跨课程汇总时，只有定义一致、证据来源一致、统计方式一致的共同维度才进入分析。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}